// ============== CONNECTED SOCIAL PLATFORMS (ADMIN) ==============
// Live status of the SITE-WIDE auto-post tokens (the fallback accounts every
// scheduled post publishes to when a user hasn't connected their own).
// Admin-only — hidden for everyone else.
import {
  useState, useEffect, axios, Button, Card, CardContent, CardHeader, CardTitle,
  Badge, Loader2, API,
} from "@/pages/shared";
import { Shield } from "lucide-react";

export const ConnectedPlatformsSection = ({ user }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/social-connections/status`);
      setStatus(r.data);
    } catch (e) {
      if (e.response?.status === 403) setStatus({ admin_only: true });
    }
    setLoading(false);
  };

  useEffect(() => { fetchStatus(); }, []);

  if (!user?.is_admin) return null;

  const platforms = [
    { key: "twitter", label: "X (Twitter)", color: "text-foreground", detail: status?.twitter?.screen_name ? `@${status.twitter.screen_name}` : status?.twitter?.error },
    { key: "linkedin", label: "LinkedIn", color: "text-blue-600", detail: status?.linkedin?.connected ? "Personal feed" : status?.linkedin?.error },
    { key: "facebook", label: "Facebook Page", color: "text-blue-500", detail: status?.facebook?.page_name || status?.facebook?.error },
    { key: "instagram", label: "Instagram", color: "text-pink-500", detail: status?.instagram?.username ? `@${status.instagram.username}${status.instagram.followers != null ? ` · ${status.instagram.followers} followers` : ""}` : status?.instagram?.error },
  ];

  return (
    <Card className="glass" data-testid="settings-connected-platforms-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-teal-400" /> Connected platforms
          <Badge className="bg-teal-500/15 text-teal-400 border border-teal-500/30 ml-2">Admin only</Badge>
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Live status of site-wide auto-post tokens. Every scheduled post that flags a platform publishes to these accounts.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : !status ? (
          <p className="text-sm text-muted-foreground">Couldn't load status.</p>
        ) : (
          <div className="space-y-2">
            {platforms.map((p) => {
              const s = status[p.key] || {};
              return (
                <div key={p.key} className="flex items-center justify-between p-3 rounded-lg bg-white/5" data-testid={`platform-status-${p.key}`}>
                  <div>
                    <p className={`font-medium ${p.color}`}>{p.label}</p>
                    <p className="text-xs text-muted-foreground">{p.detail || (s.connected ? "Connected" : "Not connected")}</p>
                  </div>
                  <Badge className={s.connected ? "bg-teal-500/20 text-teal-400" : "bg-violet-500/20 text-violet-400"} data-testid={`platform-badge-${p.key}`}>
                    {s.connected ? "✓ Connected" : "✗ Disconnected"}
                  </Badge>
                </div>
              );
            })}
            <Button variant="ghost" size="sm" onClick={fetchStatus} className="text-xs" data-testid="refresh-platforms-btn">Re-check status</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
